import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, styled } from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { showToast } from "@/redux/actions/toast-actions";
import { runtimeConfigSchema } from "@/schemas/payout.schema";
import { useUpsertRuntimeConfig } from "@/hooks/use-runtime-config";
import { CONFIG_VALUE_TYPE_OPTIONS } from "../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": { borderRadius: "12px", padding: theme.spacing(2), maxWidth: 440, width: "100%" },
}));

const ConfigFormDialog = ({ open, config, onClose }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(config);
  const mutation = useUpsertRuntimeConfig();

  const formik = useFormik({
    initialValues: {
      configKey: config?.configKey ?? "",
      value: config?.value ?? "",
      valueType: config?.valueType ?? "",
      description: config?.description ?? "",
    },
    enableReinitialize: true,
    validationSchema: runtimeConfigSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(values, {
        onSuccess: () => {
          dispatch(showToast(isEdit ? "Config updated" : "Config created", "success"));
          onClose();
        },
        onError: (err) => dispatch(showToast(err?.message ?? "Failed to save config", "error")),
      });
    },
  });

  useEffect(() => { if (open) mutation.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          {isEdit ? "Edit Config" : "Add Config"}
        </DialogTitle>

        {mutation.isError && <CustomErrorMessage message={mutation.error?.message ?? "Failed to save config"} />}

        <Stack spacing={2}>
          <FormTextInput formik={formik} name="configKey" label="Config Key" isDisabled={isEdit} />
          <FormTextInput formik={formik} name="value" label="Value" />
          <FormSelectInput formik={formik} name="valueType" label="Value Type" options={CONFIG_VALUE_TYPE_OPTIONS} />
          <FormTextArea formik={formik} name="description" label="Description (optional)" rows={2} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Saving..." : "Save"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ConfigFormDialog;